import React from "react";
import { gql } from "@apollo/client";
import { Benefits as BenefitsProps } from "../../../../__generated__/graphql";
import { ContentfulComponentType } from "../types";
import { ContentfulC2A } from "../ContentfulC2A";
import { FlexCol } from "ui-kit/Layout/Flex";
import Gutter from "ui-kit/Gutter";
import TextFieldsV2 from "../TextFieldsV2";
import FeatureCard from "./FeatureCard";
import BenefitsCarousel from "./BenefitsCarousel";
import ReviewsCarousel from "./ReviewsCarousel";
import SmallGrid from "./SmallGrid";
import styles from "./ContentfulBenefitsV2.module.css";

export const ContentfulBenefitsV2: ContentfulComponentType<BenefitsProps> = ({
  highlight,
  title,
  text,
  benefitItemsCollection,
  c2A,
  variation,
}) => {
  const items =
    benefitItemsCollection?.items.flatMap((item) => (item ? [item] : [])) ?? [];

  if (variation === "Carousel") {
    return <BenefitsCarousel title={title} text={text} items={items} />;
  }

  if (variation === "Reviews") {
    return <ReviewsCarousel title={title} text={text} items={items} />;
  }

  if (variation === "Small Grid") {
    return <SmallGrid title={title} text={text} items={items} />;
  }

  return (
    <div className="container">
      <section className={styles.section}>
        <div className={styles.textBlock}>
          <TextFieldsV2
            title={title}
            text={text}
            titleGradient
            gutterBottom={0}
          />
          {c2A && (
            <>
              <Gutter size={3} />
              <ContentfulC2A {...c2A} />
            </>
          )}
        </div>
        <div className={styles.grid}>
          {items.map((item) => (
            <FeatureCard
              key={item.title}
              title={item.title}
              description={item.description ?? null}
              text={item.text}
              color={item.color ?? null}
              image={item.image ?? null}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

ContentfulBenefitsV2.fragment = gql`
  fragment Benefits on ComponentBenefits {
    highlight
    title
    text
    benefitItemsCollection(limit: 12) {
      items {
        title
        text
        description
        icon
        color
        image {
          sys {
            id
          }
          url
          description
          width
          height
          contentType
        }
      }
    }
    benefitLayout
    listLayout
    variation
    c2A {
      ...C2A
    }
    sys {
      id
      publishedAt
    }
  }
`;
